import { UnsplashImage } from "../api/unsplash";
import { ExternalImage } from "@/types/media";
import { TreeNode } from "@/types/tree";
import { generateStockPhotoId, getUsedStockPhotoIds } from "./media-library";
import { singularityStore } from "@/store/store";

export function convertUnsplashImageToMediaInputValue(
  unsplashImage: UnsplashImage
): ExternalImage {
  return {
    id: -1,
    type: "external_image",
    url: unsplashImage.urls.regular || "",
    alt: unsplashImage.alt_description || "",
    caption: unsplashImage.description || ""
  };
}

export function findNodeById(section: TreeNode, nodeId: TreeNode["id"]): TreeNode | null {
  if (section.id === nodeId) {
    return section;
  }

  for (const child of section.children) {
    const found = findNodeById(child, nodeId);
    if (found) {
      return found;
    }
  }

  return null;
}

// Global promise to ensure only one fetch of used IDs happens at a time
let usedIdsPromise: Promise<void> | null = null;

async function ensureUsedStockPhotoIdsLoaded(abortController: AbortController): Promise<void> {
  // Ensure used stock photo IDs are loaded exactly once across all parallel calls
  if (singularityStore.usedStockPhotoIds.length === 0 && !usedIdsPromise) {
    usedIdsPromise = getUsedStockPhotoIds(abortController).then(usedStockPhotoIds => {
      singularityStore.setUsedStockPhotoIds(usedStockPhotoIds);
    });
  }

  if (usedIdsPromise) {
    await usedIdsPromise;
  }
}

export async function selectUnusedImage(
  searchResults: UnsplashImage[],
  controlPath: string,
  abortController: AbortController
): Promise<UnsplashImage> {
  await ensureUsedStockPhotoIdsLoaded(abortController);

  console.log(`IMAGE STUFF 🔍 selectUnusedImage for ${controlPath}`);
  console.log(`IMAGE STUFF 📊 Current used IDs count: ${singularityStore.usedStockPhotoIds.length}`);
  console.log(`IMAGE STUFF 📋 Current used IDs:`, singularityStore.usedStockPhotoIds.slice(0, 5), '...');

  const availableImages = searchResults.filter(image => {
    const stockPhotoId = generateStockPhotoId(image);
    const isUsed = singularityStore.usedStockPhotoIds.includes(stockPhotoId);
    console.log(`IMAGE STUFF 🖼️ Image ${stockPhotoId}: ${isUsed ? '❌ USED' : '✅ available'}`);
    return !isUsed;
  });

  console.log(`IMAGE STUFF 📈 Available images: ${availableImages.length}/${searchResults.length}`);

  let selected: UnsplashImage;
  if (availableImages.length === 0) {
    console.warn(`IMAGE STUFF ⚠️ No unused images available for ${controlPath}, using first result anyway`);
    selected = searchResults[0];
  } else {
    selected = availableImages[0];
  }

  // Mark as used immediately to prevent race conditions with parallel section generation
  const selectedStockPhotoId = generateStockPhotoId(selected);
  console.log(`IMAGE STUFF 🎯 Selected ${selectedStockPhotoId} for ${controlPath}`);

  const beforeCount = singularityStore.usedStockPhotoIds.length;
  const updatedUsedIds = [...singularityStore.usedStockPhotoIds, selectedStockPhotoId];
  singularityStore.setUsedStockPhotoIds(updatedUsedIds);
  const afterCount = singularityStore.usedStockPhotoIds.length;

  console.log(`IMAGE STUFF 📝 Store updated: ${beforeCount} → ${afterCount} used IDs`);

  return selected;
}
